import type {
  Get_Lineitems_By_Campaignid_ListQuery,
  LineItemHealthColumnsFragment,
  LineItemTraffickingColumnsFragment,
  ScheduleV5,
} from '../../../../apis/graphql';
import {
  convertAdProduct,
  convertBillableThirdParty,
  convertCostMethod,
  convertCustomPacingRequestedOption,
  convertDeliveryTier,
  convertFrequencyCapUnitType,
  convertLineItemDerivedStatus,
  convertLineItemStatus,
  convertPacingType,
  convertReview,
} from '../../../../configs';
import { convertBillingCalendarType } from '../../../../configs/billingCalendarTypes';
import type { LineItem, Nullable, OrderOmsLink, TargetingTermValue } from '../../../../models';

export type LineItemNode = NonNullable<Get_Lineitems_By_Campaignid_ListQuery['lineItems']['edges'][number]>['node'] &
  Partial<Pick<LineItemHealthColumnsFragment, 'health'>>;

export type LineItemModel = Pick<
  LineItem,
  | 'id'
  | 'name'
  | 'pacingType'
  | 'review'
  | 'goal'
  | 'status'
  | 'subStatus'
  | 'tier'
  | 'priority'
  | 'priorityValue'
  | 'adProduct'
  | 'unitCost'
  | 'unitCostDto'
  | 'costMethod'
  | 'billableThirdParty'
  | 'billableEvent'
  | 'creativeMaxSeconds'
  | 'revenue'
  | 'metrics'
  | 'startDate'
  | 'endDate'
  | 'deliveryBufferPercent'
  | 'frontLoadPercent'
  | 'frequencyCapList'
  | 'brandCapping'
  | 'lineItemSequenceId'
  | 'createdAt'
  | 'updatedAt'
  | 'trackingId'
  | 'hasZeroDeliveryRisk'
  | 'hasMisalignedSchedule'
  | 'deliveryGoal'
  | 'displayPublisherTarget'
  | 'isCoppaOrCaru'
  | 'isMakegood'
  | 'isAddedValue'
  | 'exclusiveBlock'
  | 'secondRoundEligible'
  | 'dhxpBackfill'
  | 'preemptible'
  | 'industryExclusive'
  | 'maxCreativeMs'
  | 'podBuyout'
  | 'targetingRule'
  | 'country'
  | 'countries'
  | 'pacingMetrics'
  | 'isFiller'
  | 'inventoryBlockId'
  | 'billingCalendar'
  | 'doNotTraffic'
  | 'customPacingApplied'
  | 'cpEligible'
  | 'cpRequested'
  | 'ssp'
  | 'dealMetadata'
  | 'distributionPartnerList'
  | 'automateBuffer'
  | 'automatedDeliveryBufferPercent'
  | 'staqError'
  | 'staqErrorExtended'
> & {
  campaign: Nullable<LineItemTraffickingColumnsFragment['campaign']>;
  schedule: Nullable<
    { __typename?: 'ScheduleV5' } & Pick<
      ScheduleV5,
      'timezone' | 'dateRangeList' | 'dayPartList' | 'deliveryInViewerTime'
    >
  >;
  parentLineOmsLink: Nullable<{ __typename?: 'OmsLinkV5' } & Pick<OrderOmsLink, 'id'>>;
  lineOmsLink: Nullable<OrderOmsLink>;
  orderLineItemDto: Nullable<LineItemTraffickingColumnsFragment['orderLineItemDto']>;
  magniteDealDto: Nullable<LineItemTraffickingColumnsFragment['magniteDealDto']>;
  metadata: Nullable<LineItemTraffickingColumnsFragment['metadata']>;
  draxDeal: Nullable<LineItemTraffickingColumnsFragment['draxDeal']>;
  deal: Nullable<LineItemTraffickingColumnsFragment['deal']>;
  health: Nullable<LineItemHealthColumnsFragment['health']>;
};

export function lineItemNodeToModel(node: LineItemNode): LineItemModel {
  return {
    ...node,
    adProduct: convertAdProduct(node.adProduct),
    billableThirdParty: node.billableThirdParty ? convertBillableThirdParty(node.billableThirdParty) : null,
    costMethod: node?.costMethod ? convertCostMethod(node.costMethod) : null,
    campaign: node.campaign || null,
    schedule: node.schedule || null,
    pacingType: convertPacingType(node.pacingType),
    review: convertReview(node.review),
    priorityValue: node.priorityValue || 0,
    status: convertLineItemStatus(node.status),
    subStatus: node.subStatus ? convertLineItemDerivedStatus(node.subStatus) : null,
    billingCalendar: node.campaign?.order?.billingCalendar
      ? convertBillingCalendarType(node.campaign.order.billingCalendar)
      : null,
    tier: {
      ...node.tier,
      name: convertDeliveryTier(node.tier.name),
    },
    frequencyCapList:
      node?.frequencyCapList?.map((fC) => ({
        ...fC,
        duration: fC.duration || 0,
        durationUnit: convertFrequencyCapUnitType(fC.durationUnit),
      })) || [],
    brandCapping: node.brandCapping,
    exclusiveBlock: node.exclusiveBlock
      ? {
          ...node.exclusiveBlock,
          id: node.exclusiveBlock?.id ?? null,
          name: node.exclusiveBlock?.name ?? null,
          lineItemIdList: node.exclusiveBlock?.lineItemIdList ?? [],
          blockedIndustryList: node.exclusiveBlock?.blockedIndustryList ?? [],
          targetingTermValues: (node.exclusiveBlock?.targetingTermValues ?? []) as TargetingTermValue[],
        }
      : null,
    pacingMetrics: node.pacingMetrics,
    doNotTraffic: !!node.doNotTraffic,
    customPacingApplied: !!node.customPacingApplied,
    cpRequested: convertCustomPacingRequestedOption(node.cpRequested),
    cpEligible: !!node.cpEligible,
    distributionPartnerList: node.distributionPartnerList || [],
    health: node.health ?? null,
  };
}
